import { useEffect, useRef, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { userApi } from "@/lib/api";
import { turnstileSiteKey } from "@/lib/utils/env";
import { Button, Input, Label } from "@/components/ui";
import { TurnstileWidget } from "./TurnstileWidget";

const RESEND_COOLDOWN_S = 30;

type Props = {
  /** Called with the guid the server returns once the code is verified. */
  onVerified: (guid: string, email: string) => Promise<void> | void;
  onCancel?: () => void;
  submitLabel?: string;
  autoFocus?: boolean;
};

/**
 * Two-step email sign-in: request a code, then enter the 6-digit code.
 * Shared by the login page and the "link an email" dialog (same endpoints;
 * when a session exists the server links the address to that account).
 */
export function EmailCodeForm({ onVerified, onCancel, submitLabel = "Send code", autoFocus }: Props) {
  const siteKey = turnstileSiteKey();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState("");
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);
  const submitted = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step === "code") codeRef.current?.focus();
  }, [step]);

  const trimmedEmail = email.trim().toLowerCase();
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);

  async function requestCode() {
    if (!emailValid) {
      toast.error("Enter a valid email address");
      return;
    }
    if (siteKey && !token) {
      toast.error("Please complete the verification challenge");
      return;
    }
    setSending(true);
    try {
      await userApi.requestCode(trimmedEmail, siteKey ? token : "");
      setStep("code");
      setCode("");
      submitted.current = false;
      setCooldown(RESEND_COOLDOWN_S);
      toast.success("Code sent — check your inbox");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send code");
    } finally {
      setSending(false);
      if (siteKey) {
        setToken("");
        setTurnstileReset((n) => n + 1);
      }
    }
  }

  async function verify(value: string) {
    if (value.length !== 6 || verifying || submitted.current) return;
    submitted.current = true;
    setVerifying(true);
    try {
      const res = await userApi.verifyCode(trimmedEmail, value);
      if (!res.guid) throw new Error("Invalid code");
      await onVerified(res.guid, trimmedEmail);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Invalid code");
      submitted.current = false;
      setCode("");
      codeRef.current?.focus();
    } finally {
      setVerifying(false);
    }
  }

  function onEmailSubmit(e: FormEvent) {
    e.preventDefault();
    void requestCode();
  }
  function onCodeSubmit(e: FormEvent) {
    e.preventDefault();
    void verify(code);
  }
  function onCodeChange(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    if (digits.length === 6) void verify(digits);
  }

  if (step === "email") {
    return (
      <form onSubmit={onEmailSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="auth-email">Email address</Label>
          <Input
            id="auth-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus={autoFocus}
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={sending}
          />
        </div>
        {siteKey && (
          <TurnstileWidget
            siteKey={siteKey}
            resetKey={turnstileReset}
            onVerify={setToken}
            onExpire={() => setToken("")}
            onError={() => setToken("")}
          />
        )}
        <div className="flex flex-col gap-2">
          <Button type="submit" loading={sending} disabled={!emailValid || (!!siteKey && !token)}>
            {submitLabel}
          </Button>
          {onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel} disabled={sending}>
              Cancel
            </Button>
          )}
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={onCodeSubmit} className="flex flex-col gap-4">
      <p className="text-sm text-fg-muted">
        We sent a 6-digit code to <span className="font-medium text-fg">{trimmedEmail}</span>. Enter it below to continue.
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="auth-code">Verification code</Label>
        <Input
          ref={codeRef}
          id="auth-code"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="123456"
          className="text-center text-lg tracking-[0.5em]"
          value={code}
          onChange={(e) => onCodeChange(e.target.value)}
          disabled={verifying}
        />
      </div>
      <Button type="submit" loading={verifying} disabled={code.length !== 6}>
        Verify
      </Button>
      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          className="cursor-pointer text-fg-muted hover:text-fg hover:underline"
          onClick={() => {
            setStep("email");
            setCode("");
          }}
          disabled={verifying}
        >
          Use a different email
        </button>
        {cooldown > 0 ? (
          <span className="text-fg-muted">Resend in {cooldown}s</span>
        ) : (
          <button
            type="button"
            className="cursor-pointer text-accent hover:underline disabled:opacity-50"
            onClick={() => void requestCode()}
            disabled={sending || verifying || (!!siteKey && !token)}
          >
            {sending ? "Sending…" : "Resend code"}
          </button>
        )}
      </div>
      {siteKey && cooldown === 0 && (
        <TurnstileWidget siteKey={siteKey} resetKey={turnstileReset} onVerify={setToken} onExpire={() => setToken("")} onError={() => setToken("")} />
      )}
    </form>
  );
}
